import React from "react";
import { useSettings } from "../context/SettingsContext";

/**
 * BrandTmBadge - Trademark symbol badge in a crisp luxury circle matching brand emblem.
 * 
 * @param {string} size - 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} variant - 'gold' | 'dark' | 'light' | 'navy' | 'amber'
 * @param {string} type - 'TM' | 'R' | 'NONE' (optional override)
 * @param {string} className - extra CSS classes
 */
export const BrandTmBadge = ({
  size = "sm",
  variant = "gold",
  type,
  className = "",
  style = {},
}) => {
  const { settings } = useSettings();
  const rawType = type !== undefined ? type : (settings?.trademark_type || settings?.invoice_settings?.header?.trademark_type || "TM");
  const tmType = String(rawType || "TM").toUpperCase().trim();

  // If set to NONE / HIDE, do not render trademark badge
  if (tmType === "NONE" || tmType === "OFF" || tmType === "HIDE" || tmType === "FALSE" || tmType === "NO") {
    return null;
  }

  const isRegistered = tmType === "R" || tmType === "REGISTERED" || tmType === "REG";

  const sizeMap = {
    xs: { dim: "w-2.5 h-2.5 sm:w-3 sm:h-3", top: "-top-1 sm:-top-1.5", ml: "ml-0.5" },
    sm: { dim: "w-3 h-3 sm:w-3.5 sm:h-3.5", top: "-top-1.5 sm:-top-2", ml: "ml-0.5 sm:ml-1" },
    md: { dim: "w-3.5 h-3.5 sm:w-4 sm:h-4", top: "-top-2 sm:-top-2.5", ml: "ml-1" },
    lg: { dim: "w-4.5 h-4.5 sm:w-5 sm:h-5", top: "-top-2.5 sm:-top-3", ml: "ml-1" },
    xl: { dim: "w-5.5 h-5.5", top: "-top-3.5", ml: "ml-1.5" },
  };

  const currentSize = sizeMap[size] || sizeMap.sm;

  const colorStyles = {
    gold: { stroke: "#C79A2E", fill: "#C79A2E" },
    dark: { stroke: "#D4AF37", fill: "#D4AF37" },
    light: { stroke: "#B8860B", fill: "#B8860B" },
    navy: { stroke: "#0a2140", fill: "#0a2140" },
    amber: { stroke: "#B8860B", fill: "#B8860B" },
  };

  const colors = colorStyles[variant] || colorStyles.gold;

  return (
    <span
      className={`inline-flex items-center justify-center select-none shrink-0 relative leading-none ${currentSize.dim} ${currentSize.top} ${currentSize.ml} ${className}`}
      title={isRegistered ? "Registered Trademark (®)" : "Trademark (TM)"}
      aria-label={isRegistered ? "Registered Trademark" : "Trademark"}
      style={{ verticalAlign: "super", ...style }}
    >
      <svg
        viewBox="0 0 24 24"
        className="w-full h-full block overflow-visible"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="12"
          cy="12"
          r="10.5"
          stroke={colors.stroke}
          strokeWidth="1.5"
          fill="none"
        />
        <text
          x="12"
          y="12"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={isRegistered ? "11.5" : "8.5"}
          fontWeight={isRegistered ? "800" : "600"}
          fontFamily="Arial, Helvetica, -apple-system, BlinkMacSystemFont, sans-serif"
          fill={colors.fill}
          letterSpacing={isRegistered ? "0" : "-0.2px"}
        >
          {isRegistered ? "R" : "TM"}
        </text>
      </svg>
    </span>
  );
};
